import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { useLocation } from 'react-router-dom';
import './SessionAnalysis.css';
import axios from 'axios';
import ClipLoader from 'react-spinners/ClipLoader';
import CONFIG from '../config.js';

Chart.register(...registerables);
Chart.register(annotationPlugin);

const SessionAnalysis = () => {
    const location = useLocation();
    const { identifier, userUuid } = location.state || '';

    const [loading, setLoading] = useState(true);
    const [stressDetectionChartData, setStressDetectionChartData] = useState();
    const [heartRateChartData, setHeartRateChartData] = useState();
    const [sessionStats, setSessionStats] = useState();

    const url = CONFIG.createUrl();

    const config = {headers: CONFIG.HEADERS};

    useEffect(() => {
        const fetchSessionAnalysisData = async () => {
            try {
                const response = await axios.get(`${url}stress-detection?uuid=${userUuid}&identifier=${identifier}&update_saved_data=false`, config);
                setStressDetectionChartData(response.data.hrv_chart);
                setHeartRateChartData(response.data.hr_zones_chart);
                setSessionStats(response.data.session_stats);
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        };

        fetchSessionAnalysisData();
    }, []);

    // Helper function to format seconds into hh:mm:ss
    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return [hours, minutes, secs]
            .map((val) => String(val).padStart(2, '0'))
            .join(':');
    };


    // Generate annotations for the HR zones
    const generateZoneAnnotations = (zones) => {
        return zones.map((zone) => ({
            type: 'box',
            yMin: zone.min_hr,
            yMax: zone.max_hr,
            backgroundColor: zone.color,
            borderWidth: 0,
            label: {
                content: `${zone.zone_name} (${Math.round(zone.min_hr)}-${Math.round(zone.max_hr)} BPM)`,
                enabled: true,
                position: 'right'
            }
        }));
    };

    const renderHRChart = () => {
        const { hr_data, hr_zones } = heartRateChartData;
        // Heart Rate data for the line chart
        const hrChartData = {
            labels: hr_data.map((point) => point.time),
            datasets: [
                {
                    label: 'Heart Rate (BPM)',
                    data: hr_data.map((point) => point.hr),
                    fill: false,
                    borderColor: 'blue',
                    borderWidth: 2,
                    pointRadius: 1,
                    pointBackgroundColor: 'blue',
                    tension: 0.1
                }
            ]
        };

        const hrChartOptions = {
            scales: {
                x: {
                    type: 'linear',
                    position: 'bottom',
                    title: {
                        display: true,
                        text: 'Time (hh:mm:ss)'
                    },
                    ticks: {
                        callback: function (value) {
                            return formatTime(value); // Format seconds into hh:mm:ss
                        },
                    }
                },
                y: {
                    title: {
                        display: true,
                        text: 'Heart Rate (BPM)'
                    },
                    min: 20
                }
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        title: function (context) {
                            const time = context[0].raw.x || context[0].label;
                            return `Time: ${formatTime(Number(time).toFixed(0))}`;
                        },
                        // Display the HRV value in the tooltip body
                        label: function (context) {
                            const hrValue = context.raw.y || context.raw;
                            return `HR: ${hrValue.toFixed(2)}`;
                        },
                    },
                },
                annotation: {
                    annotations: generateZoneAnnotations(hr_zones)
                },
                legend: {
                    position: 'right',
                    align: 'center',
                    labels: {
                        generateLabels: () => [
                            {
                                text: 'Heart Rate',
                                fillStyle: 'blue',
                            },
                            {
                                text: 'Zone 1',
                                fillStyle: hr_zones[0].color,
                            },
                            {
                                text: 'zone 2',
                                fillStyle: hr_zones[1].color,
                            },
                            {
                                text: 'zone 3',
                                fillStyle: hr_zones[2].color,
                            },
                            {
                                text: 'zone 4',
                                fillStyle: hr_zones[3].color,
                            },
                            {
                                text: 'zone 5',
                                fillStyle: hr_zones[4].color,
                            }
                        ],
                    },
                },
                title: {
                    display: true,
                    color: '#484a4a',
                    text: 'Heart Rate Over Time',
                },
                datalabels: {
                    display: false
                }
            }
        };

        return <Line data={hrChartData} options={hrChartOptions} />;
    }

    // Generating annotations based on state periods
    const generateAnnotations = (stateData) => {
        return stateData.map((row) => {
            let color = 'rgba(255, 255, 255, 0.25)'; // Unstressed

            if (row.state === 'Stressed') {
                color = 'rgba(255, 99, 132, 0.25)'; // Pink for stressed
            } else if (row.state === 'Recovery') {
                color = 'rgba(54, 162, 235, 0.25)'; // Blue for recovery
            }

            return {
                type: 'box',
                xMin: row.start_time,
                xMax: row.end_time,
                backgroundColor: color,
                borderColor: color
            };
        });
    };

    const renderStressDetectionChart = () => {
        const { rmssd_data, state_periods, thresholds } = stressDetectionChartData;
        // RMSSD data for the line chart
        const stressChartData = {
            labels: rmssd_data.map((point) => point.time),
            datasets: [
                {
                    label: 'HRV',
                    data: rmssd_data.map((point) => point.rmssd),
                    fill: false,
                    borderColor: 'blue',
                    borderWidth: 2,
                    pointRadius: 1,
                    pointBackgroundColor: 'blue',
                }
            ]
        };

        const stressChartOptions = {
            scales: {
                x: {
                    type: 'linear',
                    position: 'bottom',
                    title: {
                        display: true,
                        text: 'Time (hh:mm:ss)'
                    },
                    ticks: {
                        callback: function (value) {
                            return formatTime(value); // Format seconds into hh:mm:ss
                        },
                    }
                },
                y: {
                    title: {
                        display: true,
                        text: 'HRV'
                    }
                }
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        title: function (context) {
                            const time = context[0].raw.x || context[0].label;
                            return `Time: ${formatTime(time)}`;
                        },
                        // Display the HRV value in the tooltip body
                        label: function (context) {
                            const rmssdValue = context.raw.y || context.raw;
                            return `HRV: ${rmssdValue.toFixed(2)}`;
                        },
                    },
                },
                annotation: {
                    annotations: [
                        ...generateAnnotations(state_periods),
                        {
                            type: 'line',
                            borderColor: 'green',
                            borderWidth: 2,
                            borderDash: [5, 5],
                            scaleID: 'y',
                            value: thresholds.baseline_rmssd,
                            label: { content: 'Baseline HRV', enabled: true },
                        },
                        {
                            type: 'line',
                            borderColor: 'red',
                            borderWidth: 2,
                            borderDash: [5, 5],
                            scaleID: 'y',
                            value: thresholds.stress_threshold,
                            label: { content: 'Stress Threshold', enabled: true },
                        },
                        {
                            type: 'line',
                            borderColor: '#72126f',
                            borderWidth: 2,
                            borderDash: [5, 5],
                            scaleID: 'y',
                            value: thresholds.recovery_threshold,
                            label: { content: 'Recovery Threshold', enabled: true },
                        },
                    ]
                },
                legend: {
                    position: 'right',
                    align: 'center',
                    labels: {
                        generateLabels: () => [
                            {
                                text: 'HRV',
                                fillStyle: 'blue',
                            },
                            {
                                text: 'Stressed',
                                fillStyle: 'rgba(255, 99, 132, 0.25)',
                            },
                            {
                                text: 'Recovery',
                                fillStyle: 'rgba(54, 162, 235, 0.25)',
                            },
                        ],
                    },
                },
                title: {
                    display: true,
                    color: '#484a4a',
                    text: 'HRV Over Time And Stress-&-Recovery Zones',
                },
                datalabels: {
                    display: false
                }
            }
        };

        return <Line data={stressChartData} options={stressChartOptions} />;
    }

    return (
        <div className="container">
            <div className="main-content">
                <section className="card">
                    {loading &&
                        <div className="spinner-container">
                            <ClipLoader color={"#123abc"} loading={loading} size={50} />
                        </div>
                    }
                    {!loading &&
                        <div>
                            <h3 className="session-stat-header">Session Statistics</h3>
                            <div className="horizontal-metrics">
                                <div className="horizontal-metric-item">
                                    <div className="metric-value-name">HR Range</div>
                                    <div className="metric-value">{sessionStats.hr_range}</div>
                                </div>
                                <div className="horizontal-metric-item">
                                    <div className="metric-value-name">LF HF Ratio</div>
                                    <div className="metric-value">{sessionStats.lf_hf.toFixed(1)}</div>
                                </div>
                                <div className="horizontal-metric-item">
                                    <div className="metric-value-name">Mean HR</div>
                                    <div className="metric-value">{sessionStats.mean_hr.toFixed(0)}</div>
                                </div>
                                <div className="horizontal-metric-item">
                                    <div className="metric-value-name">HRV</div>
                                    <div className="metric-value">{sessionStats.rmssd.toFixed(1)}</div>
                                </div>
                            </div>

                            <div className="session-analysis-container">
                                <div className="session-chart-container">
                                    {renderHRChart()}
                                </div>
                                <div className="stress-chart-content">
                                    <h3 style={{ textAlign: 'center' }}>Stress Event Detection</h3>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Stress Start Time</th>
                                                <th>Stress End Time</th>
                                                <th>Stress Duration</th>
                                                <th>Recovery Duration</th>
                                                <th>Stress Strength</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stressDetectionChartData.stress_episode.map((episode, index) => (
                                            <tr key={`stress-episode-${index}`}>
                                                <td>{episode.Stress_Start_Time}</td>
                                                <td>{episode.Stress_End_Time}</td>
                                                <td>{episode.Stress_Duration}</td>
                                                <td>{episode.Recovery_Duration}</td>
                                                <td>{episode.Stress_Strength}</td>
                                            </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className="session-chart-container">
                                        {renderStressDetectionChart()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </section>
            </div>
        </div>
    );
}

export default SessionAnalysis;